/**
 * Shared utilities for node positioning across the mindmap.
 * Used by both manual node creation and AI suggestions.
 */

import type { Node } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'

// ============================================================================
// CONSTANTS
// ============================================================================

/** Horizontal offset when placing child/parent nodes */
const HORIZONTAL_OFFSET = 600

/** Gap between sibling nodes (added to node height) */
export const SIBLING_GAP = 40

/**
 * Fallback height when a node hasn't been measured yet (fresh insert,
 * not yet rendered). Sized for a populated idea/need node with chat,
 * CTA and prototype subsections — the tallest common case. Existing
 * rendered nodes use n.measured.height, so this only affects the
 * just-placed node and any sibling created moments earlier.
 */
export const DEFAULT_NODE_HEIGHT = 320

/** Minimum Y coordinate (ensures visibility) */
const MIN_Y = 50

/** Default X position for root nodes */
export const ROOT_X = 300

/** Default Y position for root nodes */
export const ROOT_Y = 250

// ============================================================================
// TYPES
// ============================================================================

export type Position = { x: number; y: number }

/**
 * Calculates position for a new parent node.
 * Places it to the left of the child at the same Y.
 */
export function calculateParentPosition(childNode: Node<ReactFlowNodeData>): Position {
  return {
    x: childNode.position.x - HORIZONTAL_OFFSET,
    y: Math.max(MIN_Y, childNode.position.y),
  }
}
